import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import {
  guardarRecorte,
  recorteGuardado,
  recortePadrao,
  type RecorteDoCabecalho,
} from './header-store';

/** A forma vive no store, que é quem a confere ao ler do storage. */
type FiltrosHeader = RecorteDoCabecalho;

interface HeaderContextData {
  filtros: FiltrosHeader;
  setEmpresa: (empresaId: string | null) => void;
  setEmpresaCnpjs: (empresaCnpjs: string | null) => void;
  setDataInicio: (data: string | null) => void;
  setDataFim: (data: string | null) => void;
  setFiltros: (filtros: Partial<FiltrosHeader>) => void;
  limparFiltros: () => void;
  /** Registra que houve um "Buscar" — é o que faz as outras telas abrirem já buscadas. */
  marcarAplicado: () => void;
}

const HeaderContext = createContext<HeaderContextData | undefined>(undefined);

export function HeaderProvider({ children }: { children: ReactNode }) {
  /*
    Decisão 11 da SPEC-017: o recorte escolhido uma vez vale em todas as telas e CONTINUA AO
    RECARREGAR. Antes ele vivia só na memória da página, então um F5 devolvia a pessoa aos
    "últimos 30 dias" e sem empresa — e cada tela pedia tudo de novo.

    A leitura é no inicializador do `useState`, não num efeito: num efeito, o primeiro render
    aconteceria com o padrão e as telas poderiam disparar consulta com o recorte errado antes
    de o certo chegar.
  */
  const [filtros, setFiltrosState] = useState<FiltrosHeader>(recorteGuardado);

  useEffect(() => {
    guardarRecorte(filtros);
  }, [filtros]);

  const setEmpresa = (empresaId: string | null) => {
    setFiltrosState(prev => ({ ...prev, empresaId, aplicado: false }));
  };

  const setEmpresaCnpjs = (empresaCnpjs: string | null) => {
    setFiltrosState(prev => ({ ...prev, empresaCnpjs, aplicado: false }));
  };

  const setDataInicio = (dataInicio: string | null) => {
    setFiltrosState(prev => ({ ...prev, dataInicio, aplicado: false }));
  };

  const setDataFim = (dataFim: string | null) => {
    setFiltrosState(prev => ({ ...prev, dataFim, aplicado: false }));
  };

  /*
    Mexer no recorte DESFAZ o "aplicado". Sem isso, trocar o período e navegar faria a tela
    seguinte abrir buscando — com o recorte novo que ninguém mandou buscar ainda, ou pior,
    mostrando o resultado do anterior como se fosse dele.
  */
  const setFiltros = (novosFiltros: Partial<FiltrosHeader>) => {
    setFiltrosState(prev => ({ ...prev, ...novosFiltros, aplicado: false }));
  };

  const marcarAplicado = useCallback(() => {
    setFiltrosState(prev => (prev.aplicado ? prev : { ...prev, aplicado: true }));
  }, []);

  const limparFiltros = useCallback(() => {
    setFiltrosState(recortePadrao());
  }, []);

  return (
    <HeaderContext.Provider
      value={{
        filtros,
        setEmpresa,
        setEmpresaCnpjs,
        setDataInicio,
        setDataFim,
        setFiltros,
        limparFiltros,
        marcarAplicado,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHeader() {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader deve ser usado dentro de HeaderProvider');
  }
  return context;
}
